import { Array, BigInt, Cause, Console, Effect, Fiber, pipe } from "effect";
import { isNotNullable } from "effect/Predicate";
import {
  Address,
  DashboardData,
  getChainKeyFromProtocol,
  makeUsd,
  ProtocolKeyService,
  ProtocolStateService,
} from "@silo/types";
import { getUserMarketAddresses } from "./get-user-market-addresses";
import { getRawDashboardMarket } from "./get-raw-dashboard-market";
import { getProtocolState } from "../protocol/get-protocol-state";
import { MerklClient } from "../merkl-client";
import { getDashboardMarket } from "./get-dashboard-market";
import { sortDashboardMarket } from "./utils";
import { getMarketMetaArray } from "./get-market-meta-array";
import { MerklCampaign } from "../merkl-client/types";

export type GetDashboardData = ReturnType<typeof getDashboardData>;
export type GetDashboardDataSuccess = Effect.Effect.Success<GetDashboardData>;
export type GetDashboardDataError = Effect.Effect.Error<GetDashboardData>;
export type GetDashboardDataContext = Effect.Effect.Context<GetDashboardData>;

export function getDashboardData(user: Address) {
  return Effect.gen(function* () {
    yield* Effect.logInfo("Begin 🎬: get dashboard data");

    const protocolKey = yield* ProtocolKeyService;
    const chainKey = getChainKeyFromProtocol(protocolKey);

    const merklCampaignsFiber = yield* Effect.fork(
      MerklClient.getCampaignArray({ activeOnly: true })
    );

    const [marketMetaArray, protocolState] = yield* Effect.all(
      [
        pipe(getUserMarketAddresses(user), Effect.andThen(getMarketMetaArray)),
        getProtocolState(),
      ],
      { concurrency: "unbounded" }
    );

    const rawDashboardMarketResults = yield* pipe(
      Effect.all(
        marketMetaArray.map((marketMeta) =>
          pipe(
            getRawDashboardMarket({ user, marketMeta }),
            /**
             * catchAllCause (not catchAll) so synchronous throws inside Effect
             * generators surface here as logged errors instead of bubbling up
             * as defects, where they would bypass this fallback and 500 the
             * whole dashboard with no log entry.
             */
            Effect.catchAllCause((cause) =>
              pipe(
                Effect.logError(
                  `Failed ❌: getRawDashboardMarket market=${marketMeta.marketAddress} protocol=${marketMeta.protocolKey} user=${user}: ${Cause.pretty(cause)}`
                ),
                Effect.zipRight(Effect.succeed(null))
              )
            )
          )
        ),
        { concurrency: "unbounded" }
      ),
      Effect.provideService(ProtocolStateService, protocolState)
    );

    const rawDashboardMarketArray = pipe(
      rawDashboardMarketResults,
      Array.filter(isNotNullable)
    );

    const validRawDashboardMarketArray = pipe(
      rawDashboardMarketArray,
      Array.filter(({ baseAsset, bridgeAssets }) => {
        const assets = pipe(
          [baseAsset, ...bridgeAssets],
          Array.flatMapNullable((asset) => asset)
        );

        /**
         * Keep the market if the user actually holds something in it, regardless
         * of whether every asset has a working oracle price. hasPosition() can
         * return true for markets with no live balance, so we still filter those
         * out — but we intentionally do NOT drop markets just because an asset's
         * price is unavailable (priceNative === 0n), so users can always see and
         * withdraw funds when an oracle breaks.
         */
        return assets.some(
          (asset) =>
            asset.collateralBalance > 0n ||
            asset.protectedBalance > 0n ||
            asset.debtBalance > 0n
        );
      })
    );

    const merklCampaigns = yield* pipe(
      Fiber.join(merklCampaignsFiber),
      Effect.tapError(() =>
        Effect.logWarning(
          "Failed ❌: get merkl campaign array - falling back to []"
        )
      ),
      Effect.tapError((error) => Console.error(error)),
      Effect.catchAll(() => Effect.succeed([] as MerklCampaign[]))
    );

    const dashboardMarketArray = yield* Effect.all(
      pipe(
        validRawDashboardMarketArray,
        Array.map((rawDashboardMarket) => {
          const marketMerklCampaigns = merklCampaigns.filter(
            ({ marketAddress }) =>
              marketAddress === rawDashboardMarket.marketAddress
          );

          return getDashboardMarket({
            rawDashboardMarket,
            marketMerklCampaigns,
          });
        })
      ),
      { concurrency: "unbounded" }
    ).pipe(Effect.provideService(ProtocolStateService, protocolState));

    const depositBalanceUsd = pipe(
      dashboardMarketArray,
      Array.map((market) => market.depositBalanceUsd),
      BigInt.sumAll,
      makeUsd
    );

    const debtBalanceUsd = pipe(
      dashboardMarketArray,
      Array.map((market) => market.debtBalanceUsd),
      BigInt.sumAll,
      makeUsd
    );

    const dashboardMarkets = pipe(
      dashboardMarketArray,
      Array.sort(sortDashboardMarket)
    );

    const dashboardData: DashboardData = {
      data: {
        protocolKey,
        chainKey,
        user,
        depositBalanceUsd,
        debtBalanceUsd,
        dashboardMarkets,
      },
    };

    yield* Effect.logInfo("Complete ✅: get dashboard data");

    return dashboardData;
  });
}
